/**
 * Banking React Query Hooks
 *
 * Provides hooks for Open Banking integration via TrueLayer.
 * Manages bank connections, accounts, transactions, and reconciliation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';

// =============================================================================
// TYPES (OB = Open Banking - prefixed to avoid conflict with domain/accounting)
// =============================================================================

export type OBConnectionStatus =
  | 'PENDING'
  | 'CONNECTED'
  | 'DISCONNECTED'
  | 'ERROR'
  | 'REQUIRES_REAUTH';

export type OBAccountType =
  | 'CHECKING'
  | 'SAVINGS'
  | 'CREDIT_CARD'
  | 'MORTGAGE'
  | 'LOAN'
  | 'INVESTMENT'
  | 'OTHER';

export type OBTransactionDirection = 'IN' | 'OUT';

export type OBReconciliationStatus =
  | 'UNRECONCILED'
  | 'SUGGESTED'
  | 'RECONCILED'
  | 'EXCLUDED';

export type OBSyncState =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'PARTIAL_SUCCESS'
  | 'FAILED';

export interface OBConnection {
  id: string;
  userId: string;
  accountId: string;
  provider: string;
  status: OBConnectionStatus;
  tlProviderId?: string;
  tlProviderName?: string;
  scopes?: string[];
  connectedAt?: string;
  lastSyncAt?: string;
  lastSyncError?: string;
  accountCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface OBAccount {
  id: string;
  connectionId: string;
  accountId: string;
  tlAccountId: string;
  holderName: string;
  accountType: OBAccountType;
  iban?: string;
  bic?: string;
  accountNumber?: string;
  sortCode?: string;
  currency: string;
  balance: number;
  availableBalance?: number;
  balanceUpdatedAt?: string;
  bankName?: string;
  lastTransactionSyncAt?: string;
  transactionCount: number;
  displayName?: string;
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OBTransaction {
  id: string;
  bankAccountId: string;
  accountId: string;
  tlTransactionId: string;
  transactionDate: string;
  bookingDate: string;
  amount: number;
  currency: string;
  direction: OBTransactionDirection;
  transactionType?: string;
  counterparty?: string;
  description: string;
  reference?: string;
  transactionStatus: string;
  reconciliationStatus: OBReconciliationStatus;
  linkedInvoiceId?: string;
  linkedMovementId?: string;
  linkedChartOfAccountsId?: string;
  categoryId?: string;
  categoryName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OBSyncStatus {
  id: string;
  connectionId?: string;
  bankAccountId?: string;
  state: OBSyncState;
  progress: number;
  totalItems?: number;
  syncedItems?: number;
  message?: string;
  startedAt?: string;
  completedAt?: string;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
  updatedAt: string;
}

export interface OBBalanceSnapshot {
  id: string;
  bankAccountId: string;
  balance: number;
  availableBalance?: number;
  currency: string;
  snapshotDate: string;
}

export interface OBMutationError {
  code: string;
  message: string;
}

// =============================================================================
// GRAPHQL FRAGMENTS
// =============================================================================

const BANK_CONNECTION_FIELDS = `
  id
  userId
  accountId
  provider
  status
  tlProviderId
  tlProviderName
  scopes
  connectedAt
  lastSyncAt
  lastSyncError
  accountCount
  createdAt
  updatedAt
`;

const BANK_ACCOUNT_FIELDS = `
  id
  connectionId
  accountId
  tlAccountId
  holderName
  accountType
  iban
  bic
  accountNumber
  sortCode
  currency
  balance
  availableBalance
  balanceUpdatedAt
  bankName
  lastTransactionSyncAt
  transactionCount
  displayName
  isFavorite
  createdAt
  updatedAt
`;

const BANK_TRANSACTION_FIELDS = `
  id
  bankAccountId
  accountId
  tlTransactionId
  transactionDate
  bookingDate
  amount
  currency
  direction
  transactionType
  counterparty
  description
  reference
  transactionStatus
  reconciliationStatus
  linkedInvoiceId
  linkedMovementId
  linkedChartOfAccountsId
  categoryId
  categoryName
  notes
  createdAt
  updatedAt
`;

const SYNC_STATUS_FIELDS = `
  id
  connectionId
  bankAccountId
  state
  progress
  totalItems
  syncedItems
  message
  startedAt
  completedAt
  error {
    code
    message
    retryable
  }
  updatedAt
`;

// =============================================================================
// GRAPHQL QUERIES
// =============================================================================

const GET_BANK_CONNECTIONS = `
  query GetBankConnections($accountId: ID!, $status: BankConnectionStatus, $limit: Int) {
    bankConnections(accountId: $accountId, status: $status, limit: $limit) {
      ${BANK_CONNECTION_FIELDS}
    }
  }
`;

const GET_BANK_CONNECTION = `
  query GetBankConnection($id: ID!) {
    bankConnection(id: $id) {
      ${BANK_CONNECTION_FIELDS}
    }
  }
`;

const GET_BANK_ACCOUNTS = `
  query GetBankAccounts($accountId: ID!, $connectionId: ID, $currency: String, $limit: Int, $offset: Int) {
    bankAccounts(accountId: $accountId, connectionId: $connectionId, currency: $currency, limit: $limit, offset: $offset) {
      ${BANK_ACCOUNT_FIELDS}
    }
  }
`;

const GET_BANK_ACCOUNT = `
  query GetBankAccount($id: ID!) {
    bankAccount(id: $id) {
      ${BANK_ACCOUNT_FIELDS}
    }
  }
`;

const GET_BANK_TRANSACTIONS = `
  query GetBankTransactions($filter: BankTransactionsFilter!) {
    bankTransactions(filter: $filter) {
      ${BANK_TRANSACTION_FIELDS}
    }
  }
`;

const GET_UNRECONCILED_TRANSACTIONS = `
  query GetUnreconciledTransactions($accountId: ID!, $limit: Int) {
    unreconciledTransactions(accountId: $accountId, limit: $limit) {
      ${BANK_TRANSACTION_FIELDS}
    }
  }
`;

const GET_BALANCE_HISTORY = `
  query GetBalanceHistory($bankAccountId: ID!, $fromDate: String!, $toDate: String!) {
    balanceHistory(bankAccountId: $bankAccountId, fromDate: $fromDate, toDate: $toDate) {
      id
      bankAccountId
      balance
      availableBalance
      currency
      snapshotDate
    }
  }
`;

const GET_TOTAL_BALANCE = `
  query GetTotalBalance($accountId: ID!, $currency: String) {
    totalBankBalance(accountId: $accountId, currency: $currency)
  }
`;

const GET_SYNC_STATUS = `
  query GetSyncStatus($id: ID!) {
    syncStatus(id: $id) {
      ${SYNC_STATUS_FIELDS}
    }
  }
`;

const HAS_BANK_CONNECTIONS = `
  query HasBankConnections($accountId: ID!) {
    hasBankConnections(accountId: $accountId)
  }
`;

// =============================================================================
// GRAPHQL MUTATIONS
// =============================================================================

const INIT_BANK_CONNECTION = `
  mutation InitBankConnection($input: InitBankConnectionInput!) {
    initBankConnection(input: $input) {
      success
      authUrl
      state
      connection {
        ${BANK_CONNECTION_FIELDS}
      }
      errors {
        code
        message
      }
    }
  }
`;

const COMPLETE_BANK_CONNECTION = `
  mutation CompleteBankConnection($input: CompleteBankConnectionInput!) {
    completeBankConnection(input: $input) {
      success
      connection {
        ${BANK_CONNECTION_FIELDS}
      }
      accounts {
        ${BANK_ACCOUNT_FIELDS}
      }
      syncStatus {
        ${SYNC_STATUS_FIELDS}
      }
      errors {
        code
        message
      }
    }
  }
`;

const SYNC_BANK_CONNECTION = `
  mutation SyncBankConnection($input: SyncBankConnectionInput!) {
    syncBankConnection(input: $input) {
      success
      syncId
      syncStatus {
        ${SYNC_STATUS_FIELDS}
      }
      accounts {
        ${BANK_ACCOUNT_FIELDS}
      }
      transactionCount
      errors {
        code
        message
      }
    }
  }
`;

const SYNC_BANK_ACCOUNT = `
  mutation SyncBankAccount($input: SyncBankAccountInput!) {
    syncBankAccount(input: $input) {
      success
      syncId
      syncStatus {
        ${SYNC_STATUS_FIELDS}
      }
      accounts {
        ${BANK_ACCOUNT_FIELDS}
      }
      transactionCount
      errors {
        code
        message
      }
    }
  }
`;

const DISCONNECT_BANK_CONNECTION = `
  mutation DisconnectBankConnection($input: DisconnectBankConnectionInput!) {
    disconnectBankConnection(input: $input) {
      success
      connectionId
      accountsDisconnected
      errors {
        code
        message
      }
    }
  }
`;

const RECONCILE_TRANSACTION = `
  mutation ReconcileTransaction($input: ReconcileTransactionInput!) {
    reconcileTransaction(input: $input) {
      success
      transaction {
        ${BANK_TRANSACTION_FIELDS}
      }
      errors {
        code
        message
      }
    }
  }
`;

const UNRECONCILE_TRANSACTION = `
  mutation UnreconcileTransaction($input: UnreconcileTransactionInput!) {
    unreconcileTransaction(input: $input) {
      success
      transaction {
        ${BANK_TRANSACTION_FIELDS}
      }
      errors {
        code
        message
      }
    }
  }
`;

const UPDATE_BANK_ACCOUNT = `
  mutation UpdateBankAccount($input: UpdateBankAccountInput!) {
    updateBankAccount(input: $input) {
      success
      account {
        ${BANK_ACCOUNT_FIELDS}
      }
      errors {
        code
        message
      }
    }
  }
`;

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch bank connections
 */
export function useBankConnections(
  accountId: string,
  options?: { status?: OBConnectionStatus; limit?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.banking.connections(accountId),
    queryFn: async () => {
      const result = await graphqlFetch<{ bankConnections: OBConnection[] }>(
        GET_BANK_CONNECTIONS,
        { accountId, status: options?.status, limit: options?.limit }
      );
      return result.data?.bankConnections ?? [];
    },
    enabled: options?.enabled !== false && !!accountId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single bank connection
 */
export function useBankConnection(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.banking.connection(id),
    queryFn: async () => {
      const result = await graphqlFetch<{ bankConnection: OBConnection | null }>(
        GET_BANK_CONNECTION,
        { id }
      );
      return result.data?.bankConnection ?? null;
    },
    enabled: enabled && !!id,
  });
}

/**
 * Hook to fetch bank accounts
 */
export function useBankAccounts(
  accountId: string,
  options?: {
    connectionId?: string;
    currency?: string;
    limit?: number;
    offset?: number;
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: queryKeys.banking.accounts(accountId, options?.connectionId),
    queryFn: async () => {
      const result = await graphqlFetch<{ bankAccounts: OBAccount[] }>(
        GET_BANK_ACCOUNTS,
        {
          accountId,
          connectionId: options?.connectionId,
          currency: options?.currency,
          limit: options?.limit,
          offset: options?.offset,
        }
      );
      return result.data?.bankAccounts ?? [];
    },
    enabled: options?.enabled !== false && !!accountId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch a single bank account
 */
export function useBankAccount(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.banking.account(id),
    queryFn: async () => {
      const result = await graphqlFetch<{ bankAccount: OBAccount | null }>(
        GET_BANK_ACCOUNT,
        { id }
      );
      return result.data?.bankAccount ?? null;
    },
    enabled: enabled && !!id,
  });
}

/**
 * Hook to fetch bank transactions
 */
export function useBankTransactions(
  bankAccountId: string,
  filters?: {
    fromDate?: string;
    toDate?: string;
    direction?: OBTransactionDirection;
    reconciliationStatus?: OBReconciliationStatus;
    searchQuery?: string;
    limit?: number;
    offset?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.banking.transactions(bankAccountId),
    queryFn: async () => {
      const result = await graphqlFetch<{ bankTransactions: OBTransaction[] }>(
        GET_BANK_TRANSACTIONS,
        { filter: { bankAccountId, ...filters } }
      );
      return result.data?.bankTransactions ?? [];
    },
    enabled: enabled && !!bankAccountId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch unreconciled transactions
 */
export function useUnreconciledTransactions(
  accountId: string,
  limit?: number,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.banking.unreconciledTransactions(accountId),
    queryFn: async () => {
      const result = await graphqlFetch<{ unreconciledTransactions: OBTransaction[] }>(
        GET_UNRECONCILED_TRANSACTIONS,
        { accountId, limit }
      );
      return result.data?.unreconciledTransactions ?? [];
    },
    enabled: enabled && !!accountId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch balance history
 */
export function useBalanceHistory(
  bankAccountId: string,
  fromDate: string,
  toDate: string,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.banking.balanceHistory(bankAccountId),
    queryFn: async () => {
      const result = await graphqlFetch<{ balanceHistory: OBBalanceSnapshot[] }>(
        GET_BALANCE_HISTORY,
        { bankAccountId, fromDate, toDate }
      );
      return result.data?.balanceHistory ?? [];
    },
    enabled: enabled && !!bankAccountId && !!fromDate && !!toDate,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch total balance
 */
export function useTotalBalance(
  accountId: string,
  currency?: string,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.banking.totalBalance(accountId, currency),
    queryFn: async () => {
      const result = await graphqlFetch<{ totalBankBalance: number }>(
        GET_TOTAL_BALANCE,
        { accountId, currency }
      );
      return result.data?.totalBankBalance ?? 0;
    },
    enabled: enabled && !!accountId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch sync status
 */
export function useSyncStatus(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.banking.syncStatus(id),
    queryFn: async () => {
      const result = await graphqlFetch<{ syncStatus: OBSyncStatus | null }>(
        GET_SYNC_STATUS,
        { id }
      );
      return result.data?.syncStatus ?? null;
    },
    enabled: enabled && !!id,
    refetchInterval: (query) => {
      // Poll while sync is in progress
      const status = query.state.data;
      if (status?.state === 'IN_PROGRESS' || status?.state === 'PENDING') {
        return 2000; // Poll every 2 seconds
      }
      return false;
    },
  });
}

/**
 * Hook to check if account has bank connections
 */
export function useHasBankConnections(accountId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.banking.hasBankConnections(accountId),
    queryFn: async () => {
      const result = await graphqlFetch<{ hasBankConnections: boolean }>(
        HAS_BANK_CONNECTIONS,
        { accountId }
      );
      return result.data?.hasBankConnections ?? false;
    },
    enabled: enabled && !!accountId,
    staleTime: 60 * 1000,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

interface InitBankConnectionPayload {
  initBankConnection: {
    success: boolean;
    authUrl?: string;
    state?: string;
    connection?: OBConnection;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to initialize bank connection
 */
export function useInitBankConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      provider: string;
      state: string;
      scopes?: string[];
      redirectUri: string;
    }) => {
      const result = await graphqlFetch<InitBankConnectionPayload>(
        INIT_BANK_CONNECTION,
        { input }
      );
      if (result.data?.initBankConnection.errors?.length) {
        throw new Error(result.data.initBankConnection.errors[0].message);
      }
      return result.data!.initBankConnection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface CompleteBankConnectionPayload {
  completeBankConnection: {
    success: boolean;
    connection?: OBConnection;
    accounts?: OBAccount[];
    syncStatus?: OBSyncStatus;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to complete bank connection after OAuth
 */
export function useCompleteBankConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      authCode: string;
      state: string;
      provider: string;
    }) => {
      const result = await graphqlFetch<CompleteBankConnectionPayload>(
        COMPLETE_BANK_CONNECTION,
        { input }
      );
      if (result.data?.completeBankConnection.errors?.length) {
        throw new Error(result.data.completeBankConnection.errors[0].message);
      }
      return result.data!.completeBankConnection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface SyncBankConnectionPayload {
  syncBankConnection: {
    success: boolean;
    syncId?: string;
    syncStatus?: OBSyncStatus;
    accounts?: OBAccount[];
    transactionCount?: number;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to sync bank connection
 */
export function useSyncBankConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      connectionId: string;
      fetchTransactions?: boolean;
      fromDate?: string;
      toDate?: string;
    }) => {
      const result = await graphqlFetch<SyncBankConnectionPayload>(
        SYNC_BANK_CONNECTION,
        { input }
      );
      if (result.data?.syncBankConnection.errors?.length) {
        throw new Error(result.data.syncBankConnection.errors[0].message);
      }
      return result.data!.syncBankConnection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface SyncBankAccountPayload {
  syncBankAccount: {
    success: boolean;
    syncId?: string;
    syncStatus?: OBSyncStatus;
    accounts?: OBAccount[];
    transactionCount?: number;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to sync single bank account
 */
export function useSyncBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      accountId: string;
      fromDate?: string;
      toDate?: string;
    }) => {
      const result = await graphqlFetch<SyncBankAccountPayload>(
        SYNC_BANK_ACCOUNT,
        { input }
      );
      if (result.data?.syncBankAccount.errors?.length) {
        throw new Error(result.data.syncBankAccount.errors[0].message);
      }
      return result.data!.syncBankAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface DisconnectBankConnectionPayload {
  disconnectBankConnection: {
    success: boolean;
    connectionId?: string;
    accountsDisconnected?: number;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to disconnect bank connection
 */
export function useDisconnectBankConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      connectionId: string;
      revokeToken?: boolean;
    }) => {
      const result = await graphqlFetch<DisconnectBankConnectionPayload>(
        DISCONNECT_BANK_CONNECTION,
        { input }
      );
      if (result.data?.disconnectBankConnection.errors?.length) {
        throw new Error(result.data.disconnectBankConnection.errors[0].message);
      }
      return result.data!.disconnectBankConnection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface ReconcileTransactionPayload {
  reconcileTransaction: {
    success: boolean;
    transaction?: OBTransaction;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to reconcile a transaction
 */
export function useReconcileTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      transactionId: string;
      linkedInvoiceId?: string;
      linkedMovementId?: string;
      linkedChartOfAccountsId?: string;
      notes?: string;
    }) => {
      const result = await graphqlFetch<ReconcileTransactionPayload>(
        RECONCILE_TRANSACTION,
        { input }
      );
      if (result.data?.reconcileTransaction.errors?.length) {
        throw new Error(result.data.reconcileTransaction.errors[0].message);
      }
      return result.data!.reconcileTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface UnreconcileTransactionPayload {
  unreconcileTransaction: {
    success: boolean;
    transaction?: OBTransaction;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to unreconcile a transaction
 */
export function useUnreconcileTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const result = await graphqlFetch<UnreconcileTransactionPayload>(
        UNRECONCILE_TRANSACTION,
        { input: { transactionId } }
      );
      if (result.data?.unreconcileTransaction.errors?.length) {
        throw new Error(result.data.unreconcileTransaction.errors[0].message);
      }
      return result.data!.unreconcileTransaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

interface UpdateBankAccountPayload {
  updateBankAccount: {
    success: boolean;
    account?: OBAccount;
    errors?: OBMutationError[];
  };
}

/**
 * Hook to update bank account settings
 */
export function useUpdateBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      bankAccountId: string;
      displayName?: string;
      isFavorite?: boolean;
    }) => {
      const result = await graphqlFetch<UpdateBankAccountPayload>(
        UPDATE_BANK_ACCOUNT,
        { input }
      );
      if (result.data?.updateBankAccount.errors?.length) {
        throw new Error(result.data.updateBankAccount.errors[0].message);
      }
      return result.data!.updateBankAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.banking.all() });
    },
  });
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format amount in cents to currency string
 */
export function formatBankAmount(amountInCents: number, currency = 'EUR'): string {
  const amount = amountInCents / 100;
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Get display name for bank account
 */
export function getBankAccountDisplayName(account: OBAccount): string {
  return account.displayName || account.holderName || account.iban || account.id;
}

/**
 * Get status color for connection status
 */
export function getConnectionStatusColor(status: OBConnectionStatus): string {
  switch (status) {
    case 'CONNECTED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'REQUIRES_REAUTH':
      return 'orange';
    case 'ERROR':
    case 'DISCONNECTED':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get status color for reconciliation status
 */
export function getReconciliationStatusColor(status: OBReconciliationStatus): string {
  switch (status) {
    case 'RECONCILED':
      return 'green';
    case 'SUGGESTED':
      return 'blue';
    case 'EXCLUDED':
      return 'gray';
    case 'UNRECONCILED':
    default:
      return 'yellow';
  }
}
